// NPM Imports
import React, { lazy, Suspense } from "react";
import { Provider } from "react-redux";
import { BrowserRouter, Redirect, Route, Switch } from "react-router-dom";

// Components Imports
import { Loader } from "@revvsales/components";

import PrivateRoute from "./PrivateRoute";
import PublicRoute from "./PublicRoute";
import { AppUpdateNotification, NavBarWrapper } from "../components";

// Internal Imports
import store from "../store";

// Depedency Imports
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.min";
import "react-datepicker/dist/react-datepicker.css";
import "./index.css";

// Page Imports

const Asset = lazy(() =>
  import(/* webpackChunkName: "asset" */ "../pages/Asset"),
);

const Assets = lazy(() =>
  import(/* webpackChunkName: "assets" */ "../pages/Assets"),
);

const Dashboard = lazy(() =>
  import(/* webpackChunkName: "dashboard" */ "../pages/Dashboard"),
);

const DocumentTemplate = lazy(() =>
  import(/* webpackChunkName: "documenttemplate" */ "../pages/DocumentTemplate"),
);

const StaticTemplateActions = lazy(() =>
  import(/* webpackChunkName: "statictemplateactions" */ "../pages/StaticTemplateActions"),
);

const FourOhFour = lazy(() =>
  import(/* webpackChunkName: "documenttemplate" */ "../pages/FourOhFour"),
);

const Inbox = lazy(() =>
  import(/* webpackChunkName: "inbox" */ "../pages/Inbox"),
);

const Kanban = lazy(() =>
  import(/* webpackChunkName: "kanban" */ "../pages/Kanban"),
);

const Pricebooks = lazy(() =>
  import(/* webpackChunkName: "pricebooks" */ "../pages/Pricebooks"),
);

const Products = lazy(() =>
  import(/* webpackChunkName: "products" */ "../pages/Products"),
);

const Quotations = lazy(() =>
  import(/* webpackChunkName: "quotations" */ "../pages/Quotations"),
);

const SAMLAuthentication = lazy(() =>
  import(/* webpackChunkName: "samlauthentication" */ "../pages/SAMLAuthentication"),
);

const SFSAMLAuth = lazy(() =>
  import(/* webpackChunkName: "samlauthentication" */ "../pages/SFSAMLAuth"),
);

const GoogleSheetAuth = lazy(() =>
  import(/* webpackChunkName: "samlauthentication" */ "../pages/GoogleSheetAuth"),
);

const Settings = lazy(() =>
  import(/* webpackChunkName: "settings" */ "../pages/Settings"),
);

const Templates = lazy(() =>
  import(/* webpackChunkName: "templates" */ "../pages/Templates"),
);

const WorkFlow = lazy(() =>
  import(/* webpackChunkName: "workflow" */ "../pages/WorkFlow"),
);

const SharedDocument = lazy(() =>
  import(/* webpackChunkName: "shareddocument" */ "../pages/SharedDocument"),
);

const SharedDocumentPerma = lazy(() =>
  import(/* webpackChunkName: "shareddocument" */ "../pages/SharedDocumentPerma"),
);

const Sheet = lazy(() =>
  import(/* webpackChunkName: "shareddocument" */ "../pages/Sheet"),
);

const OauthConsent = lazy(() =>
  import(/* webpackChunkName: "shareddocument" */ "../pages/OauthConsent"),
);

const NotificationBanner = lazy(() =>
  import(/* webpackChunkName: "NotificationBanner" */ "../pages/NotificationBanner"),
);

// Function to handle lazyloading
const LazyLoadingComponent = Component => props => (
  <Suspense fallback={<Loader label="Please wait..." largeLabel />}>
    <Component {...props} />
  </Suspense>
);

const App = () => (
  <BrowserRouter>
    <Provider store={store}>
      <div className="app">
        <Route path="" component={NavBarWrapper} />
        <Route path="" component={AppUpdateNotification} />
        {/* NOTE: use NotificationBanner component to show all top banner related contents */}
        <Route path="" component={LazyLoadingComponent(NotificationBanner)} />
        <Switch>
          {/* TEMP REDIRECT CODE TO HANDLE KANBAN => PIPELINE ROUTE REDIRECT */}
          <Redirect from="/kanban" to="/pipeline" />

          {/* OTHER ROUTES */}
          <PublicRoute
            path="/shared/documents/:docID/:sharingHash"
            component={LazyLoadingComponent(SharedDocument)}
          />
          <PublicRoute
            path="/perma/DOC/:sharingHash/:docName"
            component={LazyLoadingComponent(SharedDocumentPerma)} //* Permalink old url
          />
          <PublicRoute
            path="/m/:newPermalinkUrl"
            component={LazyLoadingComponent(SharedDocumentPerma)} //* Permalink new url
          />
          <PrivateRoute
            exact
            path="/products"
            component={LazyLoadingComponent(Products)}
          />
          <PrivateRoute
            path="/pricebooks/:pricebookId?"
            component={LazyLoadingComponent(Pricebooks)}
          />
          <PrivateRoute
            exact
            path={["/assets/:folderName?/:folderID?", "/sf/pipeline", "/"]}
            component={LazyLoadingComponent(Asset)}
          />
          <PrivateRoute
            path="/old-assets/:accountID?"
            component={LazyLoadingComponent(Assets)}
          />
          <PrivateRoute
            path="/dashboard"
            component={LazyLoadingComponent(Dashboard)}
          />
          <PrivateRoute path="/inbox" component={LazyLoadingComponent(Inbox)} />
          <PrivateRoute
            path="/documents/:docNo"
            component={LazyLoadingComponent(Quotations)}
          />
          <PrivateRoute
            path="/pipeline"
            component={LazyLoadingComponent(Kanban)}
          />
          <PrivateRoute
            path="/templates/:templateView?"
            component={LazyLoadingComponent(Templates)}
          />
          <PrivateRoute
            path="/workflow/:workflowId?"
            component={LazyLoadingComponent(WorkFlow)}
          />
          <PrivateRoute
            path="/template/:templateID"
            component={LazyLoadingComponent(DocumentTemplate)}
          />
          <PrivateRoute
            path="/preview/templates/:globalTemplate"
            component={LazyLoadingComponent(StaticTemplateActions)}
          />
          <PrivateRoute
            path="/settings/:settingsView/:detailView?"
            component={LazyLoadingComponent(Settings)}
          />
          <PrivateRoute path="/sheet" component={LazyLoadingComponent(Sheet)} />
          <PrivateRoute
            path="/oauth"
            component={LazyLoadingComponent(OauthConsent)}
          />
          <Route
            path="/saml/login"
            component={LazyLoadingComponent(SAMLAuthentication)}
          />
          <Route path="/search" component={LazyLoadingComponent(SFSAMLAuth)} />
          <Route
            path="/google"
            component={LazyLoadingComponent(GoogleSheetAuth)}
          />
          <Route component={LazyLoadingComponent(FourOhFour)} />
        </Switch>
      </div>
    </Provider>
  </BrowserRouter>
);

export default App;



// WEBPACK FOOTER //
// ./src/app/index.js